import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { FormGroup, FormControlLabel, Switch, TextField } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import debounce from 'lodash.debounce'; // Import debounce function

import { callOrg } from '../../services/Org';
import { deleteService, getService } from '../../services/Service';

export default function OrgList() {
  const [orgList, setOrgList] = useState([]);
  const [selectedOrgID, setSelectedOrgID] = useState(0);
  const [alertHeader, setAlertHeader] = useState('');
  const [alertBody, setAlertBody] = useState('');
  const [alertMode, setAlertMode] = useState('');
  const [openAlert, setOpenAlert] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState();
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(0);
  const [rowCount, setRowCount] = useState(0);
  const [filter, setFilter] = useState('');

  const handleClose = () => {
    setOpenAlert(false);
    setSelectedOrgID(0);
  };

  const columns = [
    { field: 'index', headerName: 'ลำดับ', maxWidth: 50, flex: 1 },
    { field: 'org_code', headerName: 'รหัสหน่วยงาน', minWidth: 250, flex: 1 },
    { field: 'org_name', headerName: 'ชื่อหน่วยงาน', minWidth: 250, flex: 1 },
    {
      field: 'status',
      headerName: 'สถานะการเปิดใช้งาน',
      sortable: false,
      maxWidth: 150,
      flex: 1,
      disableClickEventBubbling: true,
      renderCell: (cellValues) => (
        <FormGroup>
          <FormControlLabel
            control={
              <Switch
                size="small"
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: '#76BC43',
                    '&:hover': {
                      backgroundColor: 'rgba(118, 188, 67, 0.08)',
                    },
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: '#76BC43',
                  },
                }}
                checked={cellValues.row.status === 'A'}
                onClick={(event) => handleStatusSwitch(event, cellValues)}
              />
            }
            label=""
          />
        </FormGroup>
      ),
    },
    {
      field: 'edit',
      headerName: 'แก้ไข',
      sortable: false,
      maxWidth: 100,
      flex: 1,
      disableClickEventBubbling: true,
      renderCell: (cellValues) => (
        <IconButton
          sx={{ color: '#76BC43' }}
          aria-label="edit"
          onClick={(event) => handleEditClick(event, cellValues)}
        >
          <EditIcon />
        </IconButton>
      ),
    },
    {
      field: 'delete',
      headerName: 'ลบ',
      sortable: false,
      maxWidth: 100,
      flex: 1,
      disableClickEventBubbling: true,
      renderCell: (cellValues) => (
        <IconButton
          sx={{ color: '#76BC43' }}
          aria-label="delete"
          onClick={(event) => handleDeleteClick(event, cellValues)}
        >
          <DeleteIcon />
        </IconButton>
      ),
    },
  ];

  const handleStatusSwitch = (event, cellValues) => {
    let org_id = cellValues.row.id;
    let org_name = cellValues.row.org_name;
    let status = cellValues.row.status === 'A';
    let header = ' หน่วยงาน';
    let body = 'คุณต้องการ ';
    header = status ? 'ปิดการใช้งาน' + header : 'เปิดการใช้งาน' + header;
    body = status ? body + ' ปิดการใช้งาน ' : body + ' เปิดการใช้งาน ';
    body += 'หน่วยงาน ' + org_name;
    setAlertHeader(header);
    setAlertBody(body);
    setSelectedOrgID(org_id);
    setSelectedStatus(status);
    setAlertMode('status');
    setOpenAlert(true);
  };

  const handleEditClick = (event, cellValues) => {
    event.preventDefault();
    window.location.href = '/org/edit/' + cellValues.row.id;
  };

  const handleDeleteClick = (event, cellValues) => {
    event.preventDefault();
    let org_id = cellValues.row.id;
    let org_name = cellValues.row.org_name;
    let header = 'ลบข้อมูลหน่วยงาน';
    let body = 'คุณต้องการลบข้อมูล ';
    body += 'หน่วยงาน ' + org_name;
    setAlertHeader(header);
    setAlertBody(body);
    setSelectedOrgID(org_id);
    setAlertMode('delete');
    setOpenAlert(true);
  };

  const createOrgList = () => {
    const params = { page, pageSize, filter };
    getService(`organizations/get?page=${page}&pageSize=${pageSize}&filter=${filter}`)
      .then((res) => {
        const { data, total } = res.data.data;
        let temp_org_list = data.map((item, index) => ({
          id: item.org_id,
          index: page * pageSize + index + 1,
          org_name: item.org_name,
          org_code: item.org_code,
          status: item.org_status,
        }));
        setOrgList(temp_org_list);
        setRowCount(total); // assuming the API returns the total count of records
      })
      .catch((error) => {
        if (error.response.status === 401) {
          window.location.href = '/auth/login';
        }
      });
  };

  const handleAddClick = (event) => {
    event.preventDefault();
    window.location.href = '/org/add';
  };

  const handleAlertButton = () => {
    if (alertMode === 'delete') {
      deleteService('organizations/delete/' + selectedOrgID)
        .then((res) => {
          handleClose();
          createOrgList();
        })
        .catch((error) => {
          if (error.response && error.response.status === 401) {
            window.location.href = '/auth/login';
          }
        });
    } else {
      let status = selectedStatus ? 'inactive' : 'active';
      let obj = { org_id: selectedOrgID };
      callOrg('organizations/' + status, obj)
        .then((res) => {
          handleClose();
          createOrgList();
        })
        .catch((error) => {
          if (error.response && error.response.status === 401) {
            window.location.href = '/auth/login';
          }
        });
    }
  };

  useEffect(() => {
    createOrgList();
  }, [page, pageSize,filter]);

  const debouncedSearch = useCallback(
    debounce((value) => {
      setFilter(value);
      createOrgList();
    }, 300),
    [page, pageSize]
  );

  const handleFilterChange = (event) => {
    debouncedSearch(event.target.value);
  };

  return (
    <div style={{ margin: '2rem' }}>
      <AdminLayout titleName="จัดการหน่วยงาน" />
      <Grid container style={{ marginTop: '4rem', marginBottom: '2rem' }}>
        <Grid item xs>
          <Typography variant="h4" style={{ fontWeight: 'bold' }}>
            จัดการหน่วยงาน
          </Typography>
        </Grid>
        <Grid item>
          <Button
            variant="contained"
            onClick={handleAddClick}
            sx={{
              backgroundColor: '#76BC43',
              '&:hover': {
                backgroundColor: '#5e9e36',
              },
            }}
          >
            เพิ่มหน่วยงาน
          </Button>
        </Grid>
        <Grid item xs={12} style={{ marginBottom: '1rem', marginTop: '2rem' }}>
          <TextField
            label="ค้นหา"
            variant="outlined"
            sx={{ width: '50vw' }}
            onChange={handleFilterChange}
          />
        </Grid>
        <Grid item xs={12}>
          <div style={{ height: 600, width: '100%', marginTop: '2rem' }}>
            <DataGrid
              style={{ backgroundColor: '#FFFFFF' }}
              rows={orgList}
              columns={columns}
              pageSize={pageSize}
              onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
              paginationMode="server"
              rowCount={rowCount}
              onPageChange={(newPage) => setPage(newPage)}
              rowsPerPageOptions={[5, 10, 20]}
              pagination
            />
          </div>
        </Grid>
      </Grid>
      <Dialog
        open={openAlert}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{alertHeader}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {alertBody}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAlertButton} variant="contained" color="success">
            ตกลง
          </Button>
          <Button onClick={handleClose} variant="contained" color="error">
            ยกเลิก
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
